// web/src/utils/cover-line.ts
// Cover-line generator. Each match gets a punchy 3–6 word headline derived
// from its data — modelled on football-magazine cover lines like
// "RHAPSODY IN BLUE", "TRACTOR BOYS RACE INTO PREM", "RIGHT-BACK AT YA".
//
// v1: deterministic template lookup. Upgraded later (Reports tier) to LLM
// generation per match via Anthropic — same call signature, different impl.

import type { Match, MatchEventType } from '../types';

export interface CoverLineResult {
  text: string;
  /** Suggested swatch colour when no photo exists. */
  swatch: 'win' | 'draw' | 'loss' | 'milestone' | 'neutral';
}

function countEvents(m: Match, type: MatchEventType): number {
  return m.events.filter((e) => e.type === type).length;
}

/**
 * Derives the cover line for a match. Result is stable for a given match
 * record — same inputs always yield the same output, so the headline doesn't
 * "rotate" between renders.
 *
 * Reports tier: when the backend has populated `match.aiCoverLine` (set at
 * POST time by lib/cover-line.ts → Bedrock), we use that verbatim and pick
 * a swatch heuristically from the result. Free / Stats tier (and any
 * Reports match where the LLM call failed or budget was exhausted) get
 * the deterministic template lookup below.
 */
export function coverLineFor(m: Match): CoverLineResult {
  const goals = countEvents(m, 'goal');
  const assists = countEvents(m, 'assist');
  const us = m.ourScore;
  const them = m.theirScore;
  const margin =
    typeof us === 'number' && typeof them === 'number' ? us - them : null;

  // Pick a sensible swatch from the same result heuristics the deterministic
  // logic uses below, so the cover photo backdrop still matches the mood.
  const moodSwatch: CoverLineResult['swatch'] =
    goals >= 2 || m.manOfMatch
      ? 'milestone'
      : margin === null
      ? 'neutral'
      : margin > 0
      ? 'win'
      : margin < 0
      ? 'loss'
      : 'draw';

  // ── Parent's manual override beats EVERYTHING (added 2026-05-26) ─────────
  // Once a parent writes their own cover line we never second-guess it.
  if (m.coverLineOverride && m.coverLineOverride.trim().length > 0) {
    return { text: m.coverLineOverride.trim(), swatch: moodSwatch };
  }

  // ── LLM-generated cover line takes precedence over deterministic ────────
  if (m.aiCoverLine && m.aiCoverLine.trim().length > 0) {
    return { text: m.aiCoverLine.trim(), swatch: moodSwatch };
  }

  // ── Big personal milestones first (rare, deserve a headline) ─────────────
  if (goals >= 3) return { text: 'HAT-TRICK HERO', swatch: 'milestone' };
  if (goals === 2) return { text: 'BRACE OF BEAUTIES', swatch: 'milestone' };
  if (m.manOfMatch && (margin ?? 0) > 0) return { text: 'STAR OF THE SHOW', swatch: 'win' };
  if (m.manOfMatch) return { text: 'PICK OF THE BUNCH', swatch: 'milestone' };
  if (m.captain && (margin ?? 0) > 0) return { text: 'CAPTAIN, LEADER, LEGEND', swatch: 'win' };
  if (goals === 1 && assists === 0 && (m.matchType === 'cup' || m.matchType === 'tournament')) {
    return { text: 'CUP GOAL', swatch: 'milestone' };
  }

  // ── Match-result narratives ─────────────────────────────────────────────
  if (margin !== null) {
    if (margin >= 5) return { text: 'ROUT!', swatch: 'win' };
    if (margin >= 3) return { text: 'STATEMENT WIN', swatch: 'win' };
    if (margin === 2) return { text: 'COMFORTABLE THREE POINTS', swatch: 'win' };
    if (margin === 1) return { text: 'NARROW SQUEAK', swatch: 'win' };
    if (margin === 0) return { text: 'POINTS SHARED', swatch: 'draw' };
    if (margin === -1) return { text: 'CLOSE-RUN THING', swatch: 'loss' };
    if (margin >= -2) return { text: 'TOUGH ONE', swatch: 'loss' };
    if (margin >= -4) return { text: 'ONE TO FORGET', swatch: 'loss' };
    return { text: 'BRUISING DAY', swatch: 'loss' };
  }

  // ── No score recorded — generic, but tonally on-brand ────────────────────
  if (m.matchType === 'cup') return { text: 'CUP RUN GOES ON', swatch: 'neutral' };
  if (m.matchType === 'tournament') return { text: 'TOURNAMENT FORM', swatch: 'neutral' };
  if (m.matchType === 'friendly') return { text: 'FRIENDLY FIRE', swatch: 'neutral' };
  if (m.matchType === 'training') return { text: 'SESSION DONE', swatch: 'neutral' };
  return { text: 'MATCH DAY', swatch: 'neutral' };
}

/**
 * Builds the sub-deck cover-line strip ("PLUS · …") from match events.
 * Returns 0–4 short uppercase phrases, in priority order. Caller renders
 * them with ` · ` separators.
 */
export function subDeckFor(m: Match): string[] {
  const items: string[] = [];

  const goals = countEvents(m, 'goal');
  const assists = countEvents(m, 'assist');
  const saves = countEvents(m, 'save');

  if (goals > 0) items.push(`${goals} GOAL${goals === 1 ? '' : 'S'}`);
  if (assists > 0) items.push(`${assists} ASSIST${assists === 1 ? '' : 'S'}`);
  if (m.captain) items.push('CAPTAIN');
  if (m.manOfMatch) items.push('MoM');
  if (typeof m.kidRating === 'number') items.push(`${m.kidRating}/10`);
  if (saves >= 3) items.push(`${saves} SAVES`);

  return items.slice(0, 4);
}

/**
 * Maps a swatch to a CSS variable name (without the var() wrapper) so
 * callers can use it for backgrounds, borders, etc. consistently.
 */
export function swatchColorVar(swatch: CoverLineResult['swatch']): string {
  switch (swatch) {
    case 'win':       return '--result-win';
    case 'draw':      return '--result-draw';
    case 'loss':      return '--result-loss';
    case 'milestone': return '--accent-coral';
    case 'neutral':
    default:          return '--green';
  }
}
